import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { TrendingUp, Info, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '../lib/api';

const money = (n, dec = 2) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

export const DrawInterestProjection = ({ projectId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [overrides, setOverrides] = useState({});
  const [holdingBudget, setHoldingBudget] = useState(0);
  const timer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getDrawProjection(projectId);
      setData(res.data);
      setOverrides(res.data?.overrides || {});
      setHoldingBudget(res.data?.params?.holding_budget || 0);
    } catch (e) {
      console.error('draw projection load failed', e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const recompute = useCallback(async (nextOverrides, nextHolding) => {
    setRecomputing(true);
    try {
      const res = await apiService.computeDrawProjection(projectId, {
        overrides: nextOverrides,
        holding_budget: nextHolding,
      });
      setData(res.data);
    } catch (e) {
      console.error('recompute failed', e);
    } finally {
      setRecomputing(false);
    }
  }, [projectId]);

  const scheduleRecompute = useCallback((nextOverrides, nextHolding) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => recompute(nextOverrides, nextHolding), 650);
  }, [recompute]);

  const onDrawEdit = (monthKey, raw) => {
    const amount = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
    // reflect immediately in the visible row
    setData((prev) => prev && ({
      ...prev,
      months: prev.months.map((m) => (m.month_key === monthKey ? { ...m, construction_draw: amount } : m)),
    }));
    const next = { ...overrides, [monthKey]: amount };
    setOverrides(next);
    scheduleRecompute(next, holdingBudget);
  };

  const onHoldingEdit = (raw) => {
    const amount = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
    setHoldingBudget(amount);
    scheduleRecompute(overrides, amount);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiService.computeDrawProjection(projectId, {
        overrides, holding_budget: holdingBudget, save: true,
      });
      toast.success('Projection saved');
    } catch (e) {
      toast.error('Could not save projection');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setOverrides({});
    try {
      const res = await apiService.computeDrawProjection(projectId, {
        overrides: {}, holding_budget: holdingBudget, save: true,
      });
      setData(res.data);
      toast.success('Reset to schedule');
    } catch (e) {
      toast.error('Could not reset');
    }
  };

  if (loading) {
    return (
      <Card className="mt-6" data-testid="draw-projection-card">
        <CardContent className="py-10 text-center text-slate-400 text-sm">Building projection…</CardContent>
      </Card>
    );
  }

  const months = data?.months || [];
  const totals = data?.totals || {};
  const params = data?.params || {};

  return (
    <Card className="mt-6 border-indigo-200" data-testid="draw-projection-card">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              Draw &amp; Interest Projection
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              A forward projection of your remaining construction draws and the interest they accrue.
              Built from your schedule — edit any month to fit your real timeline.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={reset} data-testid="projection-reset-btn">
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="projection-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Inputs / assumptions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Stat label="Rate" value={`${params.rate_pct ?? 0}%`} accent="text-indigo-600" />
          <Stat label="Day basis" value={`${params.day_basis ?? 360}/yr`} />
          <Stat label="Drawn today" value={money(params.drawn_today, 0)} />
          <Stat label="Payments" value={(params.payment_frequency || 'monthly').replace(/^\w/, (c) => c.toUpperCase())} />
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-[11px] uppercase tracking-wide text-slate-400">Holding budget</p>
            <Input
              type="text"
              inputMode="decimal"
              value={holdingBudget}
              onChange={(e) => onHoldingEdit(e.target.value)}
              className="h-7 mt-1 px-2 text-sm font-semibold"
              data-testid="projection-holding-input"
            />
          </div>
        </div>

        {months.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm border border-dashed rounded-lg">
            No remaining construction draws to project. Add scheduled phases with budgets to build a projection.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="projection-table">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Month</th>
                  <th className="px-3 py-2 text-center font-medium">Days</th>
                  <th className="px-3 py-2 text-right font-medium">Construction draw</th>
                  <th className="px-3 py-2 text-left font-medium">What it covers</th>
                  <th className="px-3 py-2 text-right font-medium">Interest accrued</th>
                  <th className="px-3 py-2 text-right font-medium">Balance</th>
                  <th className="px-3 py-2 text-right font-medium">Payment due</th>
                </tr>
              </thead>
              <tbody>
                {months.map((m, i) => (
                  <tr
                    key={m.month_key}
                    className={`${m.is_holding ? 'bg-amber-50/60' : i % 2 ? 'bg-slate-50' : 'bg-white'} border-t border-slate-100`}
                    data-testid={`projection-row-${m.month_key}`}
                  >
                    <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">{m.label}</td>
                    <td className="px-3 py-2 text-center text-slate-500">{m.days}</td>
                    <td className="px-3 py-2 text-right">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={Number(m.construction_draw || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        onChange={(e) => onDrawEdit(m.month_key, e.target.value)}
                        className="h-7 w-28 ml-auto px-2 text-right text-sm text-blue-700 font-medium"
                        data-testid={`projection-draw-${m.month_key}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-500 max-w-[280px] truncate" title={m.covers}>{m.covers || '—'}</td>
                    <td className="px-3 py-2 text-right text-red-600">{money(m.interest_accrued)}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-700">{money(m.balance)}</td>
                    <td className="px-3 py-2 text-right font-semibold text-indigo-700">
                      {m.payment_due ? money(m.payment_due) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Totals */}
        {months.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <Stat label="Construction drawn (from today)" value={money(totals.construction_drawn_from_today, 0)} />
            <Stat label="Total projected interest" value={money(totals.total_interest, 0)} accent="text-red-600" />
            <Stat label="Holding budget left" value={money(totals.holding_budget_left, 0)}
                  accent={totals.holding_budget_left < 0 ? 'text-red-600' : 'text-green-600'} />
            <Stat label="Each month held costs ~" value={money(totals.monthly_holding_cost, 0)} accent="text-amber-600" />
          </div>
        )}

        <p className="flex items-start gap-1.5 text-[11px] text-slate-400 mt-3">
          <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" />
          {data?.note} {recomputing && <span className="text-indigo-400">· recalculating…</span>}
        </p>
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, accent = 'text-slate-800' }) => (
  <div className="rounded-lg border border-slate-200 p-3">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`text-base font-semibold mt-0.5 ${accent}`}>{value}</p>
  </div>
);

export default DrawInterestProjection;
